import { Color } from '@persona-id/design-tokens';
import { keyBy } from 'lodash/fp';
import { DateTime } from 'luxon';
import styled from 'styled-components';

import { stringifyLatitude, stringifyLongitude } from 'dashboard/lib/utils/stringFormat';
import { Device } from 'dashboard/models/device';
import { InquirySession } from 'dashboard/models/inquiry-session';
import InquiryDeviceSession from 'dashboard/views/DashboardInquiriesShowView/InquiryDeviceSession';
import InquiryDeviceSessionsSummary from 'dashboard/views/DashboardInquiriesShowView/InquiryDeviceSessionSummary';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';

const Subtitle = styled.span`
  color: ${Color.CoreSlate800};
`;

interface InquiryDeviceSessionDashboardProps {
  inquirySessions: InquirySession[];
  devices: Device[];
  isRedacted: boolean;
  onlyDisplayStartedSessions: boolean;
  toggleOnlyDisplayStartedSessions: () => void;
}

const InquiryDeviceSessionDashboard = ({
  inquirySessions,
  devices,
  isRedacted,
  onlyDisplayStartedSessions,
  toggleOnlyDisplayStartedSessions,
}: InquiryDeviceSessionDashboardProps) => {
  // NOTE: sort sessions by created date for naming - Session 1, Session 2, etc.
  const sortedSessions = inquirySessions.sort((a: InquirySession, b: InquirySession) => {
    return a.attributes['created-at'] > b.attributes['created-at'] ? 1 : -1;
  });

  return (
    <>
      <Card>
        <Card.Body>
          <InquiryDeviceSessionsSummary
            inquirySessions={sortedSessions}
            devices={devices}
            isRedacted={isRedacted}
            onlyDisplayStartedSessions={onlyDisplayStartedSessions}
            toggleOnlyDisplayStartedSessions={toggleOnlyDisplayStartedSessions}
          />
        </Card.Body>
      </Card>
      <Card>
        <Card.Body>
          <Card.Title>Sessions ({sortedSessions.length})</Card.Title>
          <Spacer height={2} />
          {sortedSessions.map((session, idx) => {
            const device = keyBy('id', devices)[session.relationships.device.data?.id];
            const isStarted = session.attributes['started-at'] != null;
            const formattedDate = DateTime.fromISO(session.attributes['created-at']).toFormat(
              DATE_AND_TIME_FORMAT,
            );
            const formattedLatLng =
              isRedacted ||
              session.attributes.latitude == null ||
              session.attributes.longitude == null
                ? null
                : `${stringifyLatitude(session.attributes.latitude)} ${stringifyLongitude(
                    session.attributes.longitude,
                  )}`;

            return (
              <Card.CollapsibleSection
                defaultExpanded={idx === 0 && isStarted}
                key={session.id}
                title={
                  <>
                    Session {idx + 1}: {formattedDate}
                    {formattedLatLng && <Subtitle>({formattedLatLng})</Subtitle>}
                    {!isStarted && <Subtitle>(Not started)</Subtitle>}
                  </>
                }
              >
                <Spacer height={3} />
                <InquiryDeviceSession
                  inquirySession={session}
                  device={device}
                  isRedacted={isRedacted}
                />
              </Card.CollapsibleSection>
            );
          })}
        </Card.Body>
      </Card>
    </>
  );
};

export default InquiryDeviceSessionDashboard;
